import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Tooltip, Spin } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/tr';
import { hourlyDataService } from '../../../services/api';

const { Text } = Typography;

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('tr');

const BrandHourlyHeatmap = ({ brandId, selectedMonth, currency = 'TRY' }) => {
  const [loading, setLoading] = useState(true);
  const [hourlyData, setHourlyData] = useState([]);
  const [hasRevenue, setHasRevenue] = useState(false);

  // Para birimi sembolü helper fonksiyonu
  const getCurrencySymbol = (curr) => {
    switch (curr) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'TRY': return '₺';
      default: return '₺';
    }
  };

  // Sayı formatlama fonksiyonu - 1.7k göstermek için
  const formatNumber = (value, isCompact = false) => {
    if (value >= 1000) {
      const thousands = value / 1000;
      if (isCompact) {
        // Hücrelerde: 1.7k formatı
        return thousands % 1 === 0 ? 
          `${thousands}k` : 
          `${thousands.toFixed(1)}k`;
      } else {
        // Tooltip'lerde: 1,745 formatı
        return Math.round(value).toLocaleString('tr-TR');
      }
    } else {
      return isCompact ? Math.round(value) : Math.round(value).toLocaleString('tr-TR');
    }
  };

  // Son 3 günün saatlik verilerini çek
  useEffect(() => {
    const fetchHourlyData = async () => {
      try {
        setLoading(true);
        
        // Bugün dahil son 3 gün
        const today = dayjs();
        const startDate = today.subtract(2, 'day'); // 2 gün geriye git (bugün dahil 3 gün)
        const endDate = today.add(1, 'day'); // Bugünü kesin dahil etmek için yarını da dahil et
        
        const data = await hourlyDataService.getHourlyData(
          brandId, 
          startDate.format('YYYY-MM-DD'), 
          endDate.format('YYYY-MM-DD')
        );
        
        // Revenue verisi var mı kontrol et
        const hasAnyRevenue = data && data.length > 0 && data.some(item => 
          item.data?.revenue !== undefined && 
          item.data?.revenue !== null && 
          Number(item.data?.revenue) > 0
        );
        
        setHourlyData(data || []);
        setHasRevenue(hasAnyRevenue);
      } catch (error) {
        console.error('Saatlik veri yüklenirken hata:', error);
        setHourlyData([]);
        setHasRevenue(false);
      } finally {
        setLoading(false);
      }
    };

    if (brandId) {
      fetchHourlyData();
    }
  }, [brandId, selectedMonth]);

  // Heatmap verisi hazırla
  const heatmapData = useMemo(() => {
    if (!hourlyData || hourlyData.length === 0) {
      return { data: {}, minValue: 0, maxValue: 0, hasData: false };
    }

    const heatmap = {};

    hourlyData.forEach(item => {
      // UTC'den Türkiye saatine çevir
      const date = dayjs.utc(item.date).tz('Europe/Istanbul');
      const day = date.format('YYYY-MM-DD');
      const hour = date.hour();
      const value = item.data?.revenue; // Sadece revenue metriğini kullan


      if (value !== undefined && value !== null) {
        const numValue = Number(value);
        if (!isNaN(numValue)) {
          if (!heatmap[day]) {
            heatmap[day] = {};
          }
          heatmap[day][hour] = numValue;
        }
      }
    });

    // Min/Max değerleri hesapla (normal saatlik değerler üzerinden)
    const values = [];
    Object.values(heatmap).forEach(dayData => {
      Object.values(dayData).forEach(value => {
        if (value !== undefined && value !== null) {
          values.push(value);
        }
      });
    });

    const minValue = values.length > 0 ? Math.min(...values) : 0;
    const maxValue = values.length > 0 ? Math.max(...values) : 0;

    return {
      data: heatmap, // Normal saatlik değerler
      minValue,
      maxValue,
      hasData: values.length > 0
    };
  }, [hourlyData]);

  // Renk hesaplama fonksiyonu
  const getHeatmapColor = (value, min, max) => {
    if (value === undefined || value === null) {
      return '#f5f5f5';
    }

    if (min === max) {
      return 'rgba(0, 255, 0, 0.6)';
    }

    // Normalize et (0-1 arası)
    const normalized = (value - min) / (max - min);
    
    // Kırmızı-Yeşil geçişi
    const r = Math.round(255 * (1 - normalized));
    const g = Math.round(255 * normalized);
    const b = 0;
    
    const opacity = 0.3 + (normalized * 0.6);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  // Saat başlıkları (0-23)
  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Son 3 günü hazırla
  const last3Days = useMemo(() => {
    const days = [];
    const today = dayjs();
    for (let i = 2; i >= 0; i--) {
      days.push(today.subtract(i, 'day').format('YYYY-MM-DD'));
    }
    return days;
  }, []);

  // Saat sütunu için kümülatif toplamları hesapla
  const getHourlyColumnTotal = (targetHour) => {
    const totals = {};
    
    last3Days.forEach(day => {
      let cumulativeTotal = 0;
      
      // 0'dan targetHour'a kadar olan tüm saatlerin toplamını hesapla
      for (let hour = 0; hour <= targetHour; hour++) {
        const value = heatmapData.data[day]?.[hour];
        if (value !== undefined && value !== null) {
          cumulativeTotal += value;
        }
      }
      
      if (cumulativeTotal > 0) {
        totals[day] = cumulativeTotal;
      }
    });
    
    return totals;
  };

  // Revenue verisi yoksa hiç gösterme (loading sırasında bile)
  if (!hasRevenue && !loading) {
    return null;
  }

  // Loading durumunda sadece revenue varsa göster
  if (loading) {
    // İlk render'da hasRevenue henüz false, bu durumda bekle
    if (!hasRevenue) {
      return null;
    }
    return (
      <div style={{ 
        background: '#fafafa', 
        padding: '16px', 
        borderRadius: '6px', 
        marginBottom: '16px',
        textAlign: 'center'
      }}>
        <Spin size="small" />
        <Text style={{ marginLeft: 8, fontSize: '12px' }}>Saatlik veriler yükleniyor...</Text>
      </div>
    );
  }

  // Yükleme bitti ve heatmap verisi yoksa hiç gösterme
  if (!loading && (!heatmapData.hasData || !hasRevenue)) {
    return null;
  }

  return (
    <div style={{ 
      background: '#fafafa', 
      padding: '12px', 
      borderRadius: '6px', 
      marginBottom: '16px' 
    }}>
      <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
        Saatlik Ciro Haritası - Son 3 Gün
      </Text>
      
      <div style={{ overflowX: 'auto' }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          fontSize: '10px'
        }}>
          <thead>
            <tr>
              <th style={{ 
                padding: '4px 2px', 
                border: '1px solid #e8e8e8',
                background: '#f0f0f0',
                textAlign: 'center',
                minWidth: '50px',
                fontSize: '9px'
              }}>
                Tarih
              </th>
              {hours.map(hour => {
                const columnTotals = getHourlyColumnTotal(hour);
                const hasColumnData = Object.keys(columnTotals).length > 0;
                
                return (
                  <th key={hour} style={{ 
                    padding: '2px 1px',
                    border: '1px solid #e8e8e8',
                    background: '#f0f0f0',
                    textAlign: 'center',
                    minWidth: '20px',
                    fontSize: '8px'
                  }}>
                    {hasColumnData ? (
                      <Tooltip 
                        title={
                          <div>
                            <div><strong>00:00 - {hour.toString().padStart(2, '0')}:00 Kümülatif Toplamlar</strong></div>
                            {Object.entries(columnTotals).map(([day, total]) => (
                              <div key={day}>
                                {dayjs(day).format('DD.MM')}: {formatNumber(total, false)} {getCurrencySymbol(currency)}
                              </div>
                            ))}
                          </div>
                        }
                      >
                        <span>
                          {hour.toString().padStart(2, '0')}
                        </span>
                      </Tooltip>
                    ) : (
                      hour.toString().padStart(2, '0')
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {last3Days.map(day => (
              <tr key={day}>
                <td style={{ 
                  padding: '4px 2px',
                  border: '1px solid #e8e8e8',
                  background: '#f8f8f8',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: '9px'
                }}>
                  <div>
                    {dayjs(day).format('DD.MM')}
                  </div>
                  <div style={{ fontSize: '8px', color: '#666' }}>
                    {dayjs(day).format('ddd')}
                  </div>
                </td>
                {hours.map(hour => {
                  const value = heatmapData.data[day]?.[hour];
                  const hasValue = value !== undefined && value !== null;
                  
                  return (
                    <td key={hour} style={{ 
                      padding: '1px',
                      border: '1px solid #e8e8e8',
                      textAlign: 'center',
                      background: getHeatmapColor(value, heatmapData.minValue, heatmapData.maxValue),
                      cursor: hasValue ? 'pointer' : 'default',
                      minWidth: '20px',
                      height: '24px'
                    }}>
                      {hasValue ? (
                        <Tooltip 
                          title={
                            <div>
                              <div><strong>Saatlik Ciro</strong></div>
                              <div>Tarih: {dayjs(day).format('DD.MM.YYYY')} {hour.toString().padStart(2, '0')}:00</div>
                              <div>Değer: {formatNumber(value, false)} {getCurrencySymbol(currency)}</div>
                            </div>
                          }
                        >
                          <div style={{ 
                            padding: '2px 1px',
                            fontSize: '8px',
                            fontWeight: 'bold',
                            color: value > (heatmapData.minValue + heatmapData.maxValue) / 2 ? 'white' : '#333',
                            lineHeight: '1'
                          }}>
                            {formatNumber(value, true)}{getCurrencySymbol(currency)}
                          </div>
                        </Tooltip>
                      ) : (
                        <div style={{ 
                          padding: '2px 1px', 
                          fontSize: '8px', 
                          color: '#ccc',
                          lineHeight: '1'
                        }}>
                          -
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Kompakt renk skalası */}
      <div style={{ 
        marginTop: '8px', 
        display: 'flex', 
        alignItems: 'center', 
        gap: '4px',
        fontSize: '9px'
      }}>
        <Text style={{ fontSize: '9px', color: '#666' }}>
          {heatmapData.minValue?.toLocaleString()}
        </Text>
        <div style={{ 
          width: '60px', 
          height: '8px',
          background: 'linear-gradient(to right, rgba(255, 0, 0, 0.7), rgba(255, 255, 0, 0.7), rgba(0, 255, 0, 0.9))',
          border: '1px solid #d9d9d9',
          borderRadius: '2px'
        }} />
        <Text style={{ fontSize: '9px', color: '#666' }}>
          {heatmapData.maxValue?.toLocaleString()}
        </Text>
      </div>
    </div>
  );
};

export default BrandHourlyHeatmap;